import React, { useEffect, useMemo, useState } from 'react';
import { version } from 'shared/constants';
import { isVersionGreater } from 'shared/utils/is-version-greater';
import { useRegisterSW } from 'virtual:pwa-register/react';

import { NewVersionMessage } from './new-version-message';
import { Version } from './types';
import { VersionContext, VersionContextProps } from './version-context';

const checkInterval = 10 * 60 * 1000;

const checkVersion = async (): Promise<Version> => {
  const versionFile = await fetch(`/version.json?${new Date().getTime()}`);

  return versionFile.json();
};

export type VersionProviderProps = {
  children: React.ReactNode;
};

export const VersionProvider: React.FC<VersionProviderProps> = ({
  children,
}) => {
  const [askedVersion, setAskedVersion] = useState(version);
  const [messageContent, setMessageContent] = useState<Version>();

  const closeMessage = () => setMessageContent(undefined);

  // check service-worker for updates
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_, r) {
      if (r) {
        setInterval(() => r.update(), checkInterval);
      }
    },
  });

  const onSubmit = () =>
    // if the service-worker does not see a difference yet, it won't reload the page.
    // So to make the page reload when the button is clicked, fallback to default reload
    needRefresh
      ? updateServiceWorker(true)
      : // @ts-ignore -- reload(true) should forceGet: https://developer.mozilla.org/en-US/docs/Web/API/Location/reload#forceget
        window.location.reload(true);

  // check the version file
  useEffect(() => {
    const check = async () => {
      try {
        const newVersion = await checkVersion();
        if (isVersionGreater(askedVersion, newVersion.version)) {
          setAskedVersion(newVersion.version);
          setMessageContent(newVersion);
        }
      } catch (e) {
        // probably bad/no internet, just wait for the next check
      }
    };

    // check every 10 minutes on an update
    const intervalRef = window.setInterval(check, checkInterval);

    check();

    return () => window.clearInterval(intervalRef);
  }, [askedVersion]);

  const value = useMemo<VersionContextProps>(
    () => ({
      newVersionAvailable: askedVersion !== version,
    }),
    [askedVersion],
  );

  return (
    <VersionContext.Provider value={value}>
      {children}
      {messageContent && (
        <NewVersionMessage
          version={messageContent}
          onCancel={closeMessage}
          onSubmit={onSubmit}
        />
      )}
    </VersionContext.Provider>
  );
};
